"use client";

import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import { handleCardKeyDown, openProfile } from "../_logic/card-actions";
import { CARD_FOCUS_RING } from "../_logic/constants";
import type { TeamMember } from "../_logic/team-members";
import { TeamCardContent } from "./TeamCardContent";

/**
 * Mobile/tablet team card: a tappable card linking to the member's profile.
 * @param props.member - The member to display.
 * @param props.index - Position in the grid, used to stagger the entrance animation.
 * @returns The rendered mobile card.
 */
export const SimpleTeamCard = ({ member, index }: { member: TeamMember; index: number }) => {
  // Hooks
  const shouldReduceMotion = useReducedMotion();

  // Render
  return (
    <motion.div
      className={`bg-white rounded-3xl p-5 w-full shadow-sm cursor-pointer flex flex-col gap-6 ${CARD_FOCUS_RING}`}
      initial={shouldReduceMotion ? false : { opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        shouldReduceMotion
          ? { duration: 0 }
          : { duration: 0.6, delay: Math.min(index, 6) * 0.08, type: "spring", stiffness: 100, damping: 15 }
      }
      whileHover={shouldReduceMotion ? undefined : { y: -4, scale: 1.02 }}
      onClick={() => openProfile(member.linkedin)}
      onKeyDown={(e) => handleCardKeyDown(e, member.linkedin)}
      role="button"
      tabIndex={0}
      aria-label={`View ${member.name}'s LinkedIn profile`}
    >
      {/* Profile Image - Square on mobile */}
      <div className="relative w-full aspect-square sm:aspect-4/5 sm:h-72 rounded-2xl bg-gray-200 overflow-hidden">
        <Image
          src={member.image}
          alt={member.name}
          fill
          sizes="(min-width: 640px) 50vw, 100vw"
          className="object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2">
        <TeamCardContent member={member} />
      </div>
    </motion.div>
  );
};
